import { useCallback } from 'react';
import { authApi } from '@/lib/auth-api';
import { useAuthStore } from '@/stores/auth-store';
import type { LoginResponse } from '@/types/api';

/** Restores a session on app start using the persisted refresh token. */
export async function bootstrapAuth(): Promise<void> {
  const state = useAuthStore.getState();
  if (state.refreshToken) {
    try {
      state.setUser(await authApi.me());
    } catch {
      state.clear();
    }
  }
  state.setBootstrapped();
}

export function useAuth() {
  const user = useAuthStore((s) => s.user);
  const bootstrapped = useAuthStore((s) => s.bootstrapped);

  const login = useCallback(async (email: string, password: string): Promise<LoginResponse> => {
    const result = await authApi.login(email, password);
    if (!result.two_factor_required) {
      useAuthStore.getState().setSession(result.tokens, result.user);
    }
    return result;
  }, []);

  /** Same shape as `login`: a session unless a second factor is still owed. */
  const signInWithGoogle = useCallback(
    async (code: string, redirectUri: string): Promise<LoginResponse> => {
      const result = await authApi.googleCallback(code, redirectUri);
      if (!result.two_factor_required) {
        useAuthStore.getState().setSession(result.tokens, result.user);
      }
      return result;
    },
    [],
  );

  const completeTwoFactor = useCallback(async (challengeToken: string, code: string) => {
    const result = await authApi.completeTwoFactor(challengeToken, code);
    useAuthStore.getState().setSession(result.tokens, result.user);
    return result;
  }, []);

  const logout = useCallback(async () => {
    const { refreshToken, clear } = useAuthStore.getState();
    if (refreshToken) {
      try {
        await authApi.logout(refreshToken);
      } catch {
        /* best effort — clear locally regardless */
      }
    }
    clear();
  }, []);

  const refreshUser = useCallback(async () => {
    useAuthStore.getState().setUser(await authApi.me());
  }, []);

  return {
    user,
    isAuthenticated: user !== null,
    bootstrapped,
    login,
    signInWithGoogle,
    completeTwoFactor,
    logout,
    refreshUser,
  };
}
